import React, { useEffect, useState, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { api } from '../api';
import LoginScreen from './LoginScreen';

/**
 * Wraps the main app. With Auth0 in front, we:
 *   1. Wait for the SDK to settle (isLoading)
 *   2. If not logged in, show LoginScreen (which kicks off the redirect)
 *   3. Otherwise, fetch an access token (audience-scoped), push it into the
 *      api client, and hit /api/auth/me to auto-create / load the local user.
 *
 * Once we have a local user we expose it on AuthContext for the rest of the app.
 */
export const AuthContext = React.createContext(null);

export default function AuthGate({ children }) {
  const {
    isLoading: auth0Loading,
    isAuthenticated,
    user: auth0User,
    getAccessTokenSilently,
    loginWithRedirect,
    logout: auth0Logout,
  } = useAuth0();

  const [state, setState] = useState({ loading: true, user: null, error: null });
  const tokenRef = useRef(null);

  // Once Auth0 finishes loading and we're authenticated, fetch a token
  // and sync with the backend.
  useEffect(() => {
    if (auth0Loading) return;
    if (!isAuthenticated) {
      setState({ loading: false, user: null, error: null });
      return;
    }
    syncWithBackend();
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [auth0Loading, isAuthenticated]);

  async function syncWithBackend() {
    setState({ loading: true, user: null, error: null });
    try {
      const token = await getAccessTokenSilently();
      tokenRef.current = token;
      // Stash the token where the api client reads it. The api client already
      // checks localStorage for siphon_jwt; we hijack that key.
      localStorage.setItem('siphon_jwt', token);
      const me = await api.me();
      setState({ loading: false, user: me, error: null });
    } catch (e) {
      console.error('Auth sync failed:', e);
      setState({ loading: false, user: null, error: e.message || 'Auth sync failed' });
    }
  }

  // Keep the token fresh — Auth0 SDK handles silent refresh, but make sure
  // the api client always sees the latest. Re-fetches every 5 min.
  useEffect(() => {
    if (!isAuthenticated) return;
    const id = setInterval(async () => {
      try {
        const t = await getAccessTokenSilently();
        if (t && t !== tokenRef.current) {
          tokenRef.current = t;
          localStorage.setItem('siphon_jwt', t);
        }
      } catch {/* ignore */}
    }, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, [isAuthenticated, getAccessTokenSilently]);

  if (auth0Loading || state.loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-900 text-slate-400">
        Loading…
      </div>
    );
  }

  if (!isAuthenticated || !state.user) {
    return (
      <LoginScreen
        error={state.error}
        onLogin={() => loginWithRedirect()}
      />
    );
  }

  return (
    <AuthContext.Provider value={{
      user: state.user,
      auth0User,
      refresh: syncWithBackend,
      logout: () => {
        api.clearTokens();
        auth0Logout({ logoutParams: { returnTo: window.location.origin } });
      },
    }}>
      {children}
    </AuthContext.Provider>
  );
}
